"use client";

import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import { ArrowLeft, User, Calendar } from "lucide-react";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import { parse } from "node-html-parser";

import "vidstack/player/styles/base.css";
import "vidstack/player/styles/plyr/theme.css";
import "vidstack/player";
import "vidstack/player/layouts/plyr";
import "vidstack/player/ui";

const formatURL = (url) => {
  if (!url) return "";
  return url.replace(
    "https://combatwarriors.gg",
    "https://blog.combatwarriors.gg"
  );
};

export default function Home({ post }) {
  const router = useRouter();

  const desc = post?.meta_description || post?.excerpt || "";
  const trimmedDesc = desc.length > 200 ? desc.slice(0, 200) + "..." : desc;

  return (
    <>
      <Head>
        <title>{post.title} - Combat Warriors</title>
        <meta name="description" content={trimmedDesc} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={trimmedDesc} />
        {post?.feature_image && (
          <meta property="og:image" content={post.feature_image} />
        )}
        {post?.feature_image && (
          <meta property="twitter:card" content="summary_large_image" />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      {/* Wrapper */}
      <div className="relative min-h-screen bg-black text-white overflow-hidden pt-16">
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
          {/* Navigation */}
          <div className="mb-6">
            <button
              onClick={() => router.push("/blog")}
              className="inline-flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 hover:border-white/20 transition-all duration-300 group backdrop-blur-sm"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
              <span className="text-sm">Back to posts</span>
            </button>
          </div>

          {/* Hero Section */}
          <div className="mb-6">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm group">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000 animate-shimmer" />
              <div className="relative aspect-[21/11] min-h-[500px] overflow-hidden">
                <Image
                  src={
                    formatURL(post.feature_image) ||
                    "/placeholder.svg?height=600&width=1200&query=futuristic blog hero"
                  }
                  alt="Blog post hero image"
                  width={1200}
                  height={1000}
                  className="w-full h-full object-cover transition-transform duration-700"
                  priority
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="space-y-4" style={{ zIndex: 10 }}>
                  <h1 className="text-4xl md:text-5xl font-bold leading-tight bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                    {post.title}
                  </h1>
                  <div className="flex items-center gap-4">
                    <div className="relative group">
                      {post?.primary_author?.profile_image ? (
                        <Image
                          src={
                            formatURL(post.primary_author.profile_image) ||
                            "/placeholder.svg"
                          }
                          alt="Author avatar"
                          width={60}
                          height={60}
                          className="rounded-full border-2 border-white/20 transition-all duration-300 group-hover:border-white/40"
                        />
                      ) : (
                        <div className="w-15 h-15 bg-white/10 border-2 border-white/20 rounded-full flex items-center justify-center ">
                          <User className="w-8 h-8 text-white/60" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-white/80">
                      <p className="text-lg">{post.primary_author.name}</p>
                      <span className="text-white/30">•</span>
                      <div className="flex items-center gap-2 hover:text-white/80 transition-colors duration-300">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {dayjs(post.published_at).format("YYYY.MM.DD")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {post?.isUpdate && (
            <div className="mb-6 p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm text-md">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  This update is now live! Head over to the game to see what
                  we've been working on.
                </div>
                <a
                  href="https://playcw.gg?utm_source=blog&utm_medium=referral&utm_campaign=update_banner"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/10 hover:border-white transition-all duration-300 text-center"
                >
                  Play the Update
                </a>
              </div>
            </div>
          )}

          {/* Content */}
          <article className="relative">
            <div className="relative p-6 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm">
              <div
                className="prose prose-invert prose-base max-w-none
                prose-headings:font-semibold prose-headings:text-white prose-headings:border-b prose-headings:border-white/10 prose-headings:pb-2 prose-headings:mb-4
                prose-p:text-white/80 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-white prose-a:underline prose-a:decoration-white/30 hover:prose-a:decoration-white/60 prose-a:transition-all prose-a:duration-300
                prose-strong:text-white prose-strong:font-medium
                prose-code:bg-white/10 prose-code:text-white prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:text-xs prose-code:border prose-code:border-white/20
                prose-pre:bg-white/5 prose-pre:border prose-pre:border-white/10 prose-pre:rounded-lg prose-pre:p-3
                prose-blockquote:border-l-4 prose-blockquote:border-white/30 prose-blockquote:bg-white/5 prose-blockquote:rounded-r-lg prose-blockquote:p-3 prose-blockquote:my-4
                prose-img:rounded-lg prose-img:border prose-img:border-white/10 prose-img:shadow-xl
                prose-hr:border-white/20 prose-hr:my-6
                prose-ul:text-white/80 prose-ol:text-white/80
                prose-li:text-white/80 prose-li:mb-1.5"
                dangerouslySetInnerHTML={{ __html: post.html }}
              />
            </div>
          </article>
        </div>
      </div>
    </>
  );
}

export async function getServerSideProps(ctx) {
  const slug = ctx.query.slug;
  const res = await fetch(`${process.env.SITE_URL}/api/v1/blog/${slug}`);
  const post = await res.json().then((data) => data?.data);

  if (!post) {
    return {
      notFound: true,
    };
  }

  const formatHtml = (html) => {
    html = html.replace(
      /<video(.*?)>/g,
      "<media-player autoplay muted loop $1><media-provider></media-provider><media-plyr-layout></media-plyr-layout></media-player>"
    );

    html = html.replace(
      /<div class=\"kg-video-container\">/gim,
      `<div class=\"blog-video-container\">`
    );

    const root = parse(html);

    const allowedKgClasses = [
      "kg-card",
      "kg-video-card",
      "kg-embed-card",
      "kg-image-card",
      "kg-width-regular",
      "kg-card-hascaption",
    ];
    root.querySelectorAll("*").forEach((element) => {
      const classes = element.getAttribute("class");
      if (!classes) return;
      const isAKgClass = classes
        .split(" ")
        .some((c) => c.startsWith("kg-") && !allowedKgClasses.includes(c));
      if (isAKgClass) {
        element.remove();
      }
    });

    return root.toString();
  };

  const html = formatHtml(post.html);
  const isUpdate = post.tags.some(
    (tag) => tag.name.toLowerCase() === "updates"
  );

  return {
    props: {
      post: {
        title: post.title,
        feature_image: post.feature_image,
        published_at: post.published_at,
        excerpt: post.excerpt,
        meta_description: post.meta_description,
        meta_title: post.meta_title,
        html,
        primary_author: {
          name: post?.primary_author?.name,
          profile_image: post?.primary_author?.profile_image,
        },
        isUpdate,
      },
    },
  };
}
